import { useQuery } from '@tanstack/react-query';
import { ReactFlowProvider } from '@xyflow/react';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

import { BuilderPage } from '@/app/builder';
import { AdminBuilderHeader } from '@/app/builder/admin-builder-header';
import { BuilderStateProvider } from '@/app/builder/builder-state-provider';
import { LoadingSpinner } from '@/components/ui/spinner';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminConsentGate } from '@/features/internal-admin/lib/use-admin-consent-gate';
import { AdminExecutionDetail } from '@activepieces/shared';

const AdminExecutionViewerPage = () => {
  const { tenantId, runId } = useParams();
  const { canLoadCustomerData, isClientError, consentDialog } =
    useAdminConsentGate(tenantId);

  const { data, isError } = useQuery<AdminExecutionDetail, Error>({
    queryKey: ['admin-execution', tenantId, runId],
    queryFn: () => adminApi.getExecution(tenantId!, runId!),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: canLoadCustomerData && !!runId,
  });

  if (consentDialog) {
    return consentDialog;
  }

  if (isClientError || isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Execution not found')}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-background flex h-screen w-screen items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <BuilderStateProvider
        flow={data.flow}
        flowVersion={data.flow.version}
        readonly={true}
        run={data.run}
        canExitRun={false}
        sampleData={{}}
      >
        <BuilderPage
          customHeader={
            <AdminBuilderHeader flowName={data.flow.version.displayName} />
          }
        />
      </BuilderStateProvider>
    </ReactFlowProvider>
  );
};

export default AdminExecutionViewerPage;
